import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Wallet, Building2, Plus, Trash2, ArrowDownToLine, Loader2,
  CheckCircle, Clock, AlertCircle, CreditCard, History, Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const NIGERIAN_BANKS = [
  { code: '044', name: 'Access Bank' },
  { code: '023', name: 'Citibank Nigeria' },
  { code: '050', name: 'Ecobank Nigeria' },
  { code: '070', name: 'Fidelity Bank' },
  { code: '011', name: 'First Bank of Nigeria' },
  { code: '214', name: 'First City Monument Bank' },
  { code: '058', name: 'Guaranty Trust Bank' },
  { code: '030', name: 'Heritage Bank' },
  { code: '076', name: 'Polaris Bank' },
  { code: '101', name: 'Providus Bank' },
  { code: '221', name: 'Stanbic IBTC Bank' },
  { code: '068', name: 'Standard Chartered Bank' },
  { code: '232', name: 'Sterling Bank' },
  { code: '032', name: 'Union Bank of Nigeria' },
  { code: '033', name: 'United Bank for Africa' },
  { code: '215', name: 'Unity Bank' },
  { code: '035', name: 'Wema Bank' },
  { code: '057', name: 'Zenith Bank' },
  { code: '999', name: 'Other' },
];

const STRIPE_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', country: 'United States' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', country: 'Canada' },
  { code: 'GBP', symbol: '£', name: 'British Pound', country: 'United Kingdom' },
  { code: 'EUR', symbol: '€', name: 'Euro', country: 'Europe' },
];

const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

const getRoutingLabel = (currency) => {
  if (currency === 'USD') return 'Routing Number (ABA)';
  if (currency === 'CAD') return 'Transit / Routing Number';
  if (currency === 'GBP') return 'Sort Code';
  if (currency === 'EUR') return 'IBAN';
  return 'Routing Number';
};

const getAccountLabel = (currency) => {
  if (currency === 'EUR') return 'SWIFT / BIC Code';
  return 'Account Number';
};

export default function EarningsPage() {
  const { user, isOrganizer, isAdmin, loading: authLoading } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [bankingInfo, setBankingInfo] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBankDialog, setShowAddBankDialog] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [accountVerified, setAccountVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('paystack');

  const defaultBankForm = {
    bank_name: '', bank_code: '', account_name: '', account_number: '',
    currency: 'NGN', country: '', routing_number: '', swift_code: '',
    payout_method: 'paystack', account_type: 'checking', password: '',
    otp_code: ''
  };
  const [bankForm, setBankForm] = useState(defaultBankForm);
  const [withdrawAmount, setWithdrawAmount] = useState('');

  // OTP step-up state shared by Add and Delete dialogs.
  // `otpCooldown` counts down from 60s after each send so users can't spam.
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Delete-bank dialog state — separate password + OTP challenge (backend
  // now guards DELETE /banking/{id} with the same password + OTP as save).
  const [deleteDialog, setDeleteDialog] = useState({
    open: false, bankingId: null, password: '', otp_code: '', submitting: false,
  });

  // Bank-account approval gate state. Status: "none" | "pending" | "approved" | "rejected"
  // Admins are implicitly approved (existing behaviour); we only gate organizers.
  const [bankRequestStatus, setBankRequestStatus] = useState({ status: 'approved' });
  const [bankRequestLoading, setBankRequestLoading] = useState(false);
  const [bankRequestSubmitting, setBankRequestSubmitting] = useState(false);

  const fetchBankRequestStatus = async () => {
    setBankRequestLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/organizer/bank-account-request/status`);
      setBankRequestStatus(res.data || { status: 'none' });
    } catch {
      // 403 (admin/other role) → don't gate. Keep as "approved" so the form renders normally.
      setBankRequestStatus({ status: 'approved' });
    } finally {
      setBankRequestLoading(false);
    }
  };

  const submitBankRequest = async () => {
    setBankRequestSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/organizer/bank-account-request`, {});
      toast.success('Request submitted. We will notify you when it has been reviewed.');
      fetchBankRequestStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setBankRequestSubmitting(false);
    }
  };

  // Only the organizer flow is gated. Admins bypass entirely.
  const isBankFormEnabled = isAdmin || bankRequestStatus.status === 'approved';

  useEffect(() => {
    if (isOrganizer || isAdmin) fetchData();
    if (isOrganizer) fetchBankRequestStatus();
  }, [isOrganizer, isAdmin]);

  // Countdown for the "resend OTP" button so we don't hammer Twilio.
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  if (!authLoading && user && !isOrganizer && !isAdmin) {
    return <Navigate to="/my-tickets" replace />;
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const [earningsRes, bankingRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizer/earnings`),
        axios.get(`${API_URL}/api/organizer/banking`),
        axios.get(`${API_URL}/api/organizer/withdrawals`)
      ]);
      setEarnings(earningsRes.data);
      setBankingInfo(bankingRes.data);
      setWithdrawals(withdrawalsRes.data);
    } catch {
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setBankForm(defaultBankForm);
    setAccountVerified(false);
    setVerificationError('');
    setPayoutMethod('paystack');
    setOtpSent(false);
    setOtpCooldown(0);
  };

  const sendBankingOTP = async () => {
    setOtpSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/organizer/banking/send-otp`);
      const channel = res?.data?.channel === 'whatsapp' ? ' via WhatsApp' : '';
      toast.success(`OTP sent to your registered phone${channel}`, { duration: 6000 });
      setOtpSent(true);
      setOtpCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP. Please try again.', { duration: 6000 });
    } finally {
      setOtpSending(false);
    }
  };

  const handlePayoutMethodSwitch = (method) => {
    setPayoutMethod(method);
    setAccountVerified(false);
    setVerificationError('');
    if (method === 'paystack') {
      setBankForm({ ...defaultBankForm, payout_method: 'paystack', currency: 'NGN' });
    } else {
      setBankForm({ ...defaultBankForm, payout_method: 'stripe', currency: 'USD', country: 'United States' });
    }
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    if (!bankForm.password) { toast.error('Please enter your password'); return; }
    if (!bankForm.otp_code || bankForm.otp_code.length < 4) {
      toast.error('Please enter the OTP sent to your phone'); return;
    }
    if (payoutMethod === 'paystack' && !accountVerified) { toast.error('Please wait for account verification'); return; }
    if (payoutMethod === 'stripe' && (!bankForm.account_name || !bankForm.account_number || !bankForm.routing_number)) {
      toast.error('Please fill all required fields'); return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/organizer/banking`, bankForm);
      toast.success('Bank account added successfully');
      setBankingInfo([...bankingInfo, response.data.banking]);
      setShowAddBankDialog(false);
      resetDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (bankingId) => {
    setDeleteDialog({ open: true, bankingId, password: '', otp_code: '', submitting: false });
    // OTP state is shared with Add — reset so the user can't reuse a stale send.
    setOtpSent(false);
    setOtpCooldown(0);
  };

  const handleDeleteBank = async () => {
    if (!deleteDialog.password) { toast.error('Please enter your password'); return; }
    if (!deleteDialog.otp_code || deleteDialog.otp_code.length < 4) {
      toast.error('Please enter the OTP sent to your phone'); return;
    }
    setDeleteDialog((d) => ({ ...d, submitting: true }));
    try {
      // Axios `delete` supports a body via `data:` — required by the new
      // DeleteBankingRequest model (password + otp_code).
      await axios.delete(`${API_URL}/api/organizer/banking/${deleteDialog.bankingId}`, {
        data: { password: deleteDialog.password, otp_code: deleteDialog.otp_code },
      });
      toast.success('Bank account deleted');
      setBankingInfo(bankingInfo.filter((b) => b.banking_id !== deleteDialog.bankingId));
      setDeleteDialog({ open: false, bankingId: null, password: '', otp_code: '', submitting: false });
      setOtpSent(false);
      setOtpCooldown(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bank account');
      setDeleteDialog((d) => ({ ...d, submitting: false }));
    }
  };

  const verifyBankAccount = async (accountNumber, bankCode) => {
    if (!accountNumber || accountNumber.length !== 10 || !bankCode) return;
    setVerifyingAccount(true);
    setAccountVerified(false);
    setVerificationError('');
    try {
      const response = await axios.post(
        `${API_URL}/api/organizer/banking/verify-account`,
        { account_number: accountNumber, bank_code: bankCode },
        
      );
      if (response.data.verified) {
        setBankForm(prev => ({ ...prev, account_name: response.data.account_name }));
        setAccountVerified(true);
        toast.success(`Account verified: ${response.data.account_name}`);
      } else {
        setVerificationError(response.data.message || 'Could not verify account');
      }
    } catch (error) {
      setVerificationError(error.response?.data?.detail || 'Failed to verify account');
    } finally {
      setVerifyingAccount(false);
    }
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, payoutMethod === 'paystack' ? 10 : 34);
    setBankForm(prev => ({ ...prev, account_number: value, account_name: payoutMethod === 'paystack' ? '' : prev.account_name }));
    if (payoutMethod === 'paystack') {
      setAccountVerified(false);
      setVerificationError('');
      if (value.length === 10 && bankForm.bank_code) verifyBankAccount(value, bankForm.bank_code);
    }
  };

  const handleBankChange = (bankCode) => {
    const selectedBank = NIGERIAN_BANKS.find(b => b.code === bankCode);
    setBankForm(prev => ({ ...prev, bank_code: bankCode, bank_name: selectedBank?.name || '', account_name: '' }));
    setAccountVerified(false);
    setVerificationError('');
    if (bankForm.account_number.length === 10) verifyBankAccount(bankForm.account_number, bankCode);
  };

  const handleStripeCurrencyChange = (currencyCode) => {
    const curr = STRIPE_CURRENCIES.find(c => c.code === currencyCode);
    setBankForm(prev => ({ ...prev, currency: currencyCode, country: curr?.country || '' }));
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/organizer/withdraw`, { amount: parseFloat(withdrawAmount) });
      toast.success('Withdrawal request submitted');
      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': case 'approved':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20"><AlertCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto py-8" data-testid="earnings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Earnings & Withdrawals</h1>
          <p className="text-muted-foreground">Manage your earnings and withdraw funds to your bank account</p>
        </div>

        {/* Earnings Overview */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10"><Wallet className="w-5 h-5 text-green-500" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Earnings</p>
                  <p className="text-2xl font-bold" data-testid="total-earnings">{earnings?.total_earnings?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10"><ArrowDownToLine className="w-5 h-5 text-blue-500" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Withdrawn</p>
                  <p className="text-2xl font-bold">{earnings?.total_withdrawn?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10"><Clock className="w-5 h-5 text-yellow-500" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold">{earnings?.pending_withdrawals?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-primary">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10"><CreditCard className="w-5 h-5 text-primary" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Available</p>
                  <p className="text-2xl font-bold text-primary">{earnings?.available_balance?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Banking Information */}
        <Card className="mb-8" data-testid="banking-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Payout Accounts
              </CardTitle>
              <CardDescription>Add your bank account to receive payouts</CardDescription>
            </div>
            <Dialog open={showAddBankDialog && isBankFormEnabled} onOpenChange={(open) => { if (!isBankFormEnabled) return; setShowAddBankDialog(open); if (!open) resetDialog(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-bank-btn" disabled={!isBankFormEnabled} title={!isBankFormEnabled ? 'Approval required before adding bank info' : undefined}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Payout Account</DialogTitle>
                  <DialogDescription>Select your payout method and enter bank details</DialogDescription>
                </DialogHeader>

                {/* Payout Method Selector */}
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <button
                    type="button"
                    onClick={() => handlePayoutMethodSwitch('paystack')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      payoutMethod === 'paystack'
                        ? 'border-[#00c3f7] bg-[#00c3f7]/10'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                    data-testid="payout-method-paystack"
                  >
                    <p className="font-semibold text-sm">Paystack</p>
                    <p className="text-xs text-muted-foreground">Nigerian bank (NGN)</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePayoutMethodSwitch('stripe')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      payoutMethod === 'stripe'
                        ? 'border-[#635bff] bg-[#635bff]/10'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                    data-testid="payout-method-stripe"
                  >
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-sm">Stripe</p>
                      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">International (USD, CAD, GBP, EUR)</p>
                  </button>
                </div>

                <form onSubmit={handleAddBank} className="space-y-4">
                  {/* PAYSTACK FORM */}
                  {payoutMethod === 'paystack' && (
                    <>
                      <div className="space-y-2">
                        <Label>Bank</Label>
                        <Select value={bankForm.bank_code} onValueChange={handleBankChange}>
                          <SelectTrigger data-testid="bank-select">
                            <SelectValue placeholder="Select your bank" />
                          </SelectTrigger>
                          <SelectContent>
                            {NIGERIAN_BANKS.map(bank => (
                              <SelectItem key={bank.code} value={bank.code}>{bank.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Account Number</Label>
                        <div className="relative">
                          <Input
                            value={bankForm.account_number}
                            onChange={handleAccountNumberChange}
                            placeholder="Enter 10-digit account number"
                            maxLength={10}
                            required
                            data-testid="account-number-input"
                            className={verificationError ? 'border-red-500' : accountVerified ? 'border-green-500' : ''}
                          />
                          {verifyingAccount && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            </div>
                          )}
                          {accountVerified && !verifyingAccount && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            </div>
                          )}
                        </div>
                        {verificationError && <p className="text-xs text-red-500">{verificationError}</p>}
                        {bankForm.account_number.length > 0 && bankForm.account_number.length < 10 && (
                          <p className="text-xs text-muted-foreground">{10 - bankForm.account_number.length} digits remaining</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Account Name</Label>
                        <div className="relative">
                          <Input
                            value={bankForm.account_name}
                            placeholder={verifyingAccount ? "Verifying..." : accountVerified ? bankForm.account_name : "Will auto-fill after verification"}
                            required readOnly={accountVerified}
                            onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                            data-testid="account-name-input"
                            className={accountVerified ? 'bg-green-50 border-green-500 text-green-700 font-medium' : ''}
                          />
                          {accountVerified && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            </div>
                          )}
                        </div>
                        {accountVerified && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Account verified
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {/* STRIPE (INTERNATIONAL) FORM */}
                  {payoutMethod === 'stripe' && (
                    <>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select value={bankForm.currency} onValueChange={handleStripeCurrencyChange}>
                          <SelectTrigger data-testid="stripe-currency-select">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            {STRIPE_CURRENCIES.map(c => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.symbol} {c.code} — {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Account Holder Name</Label>
                        <Input
                          value={bankForm.account_name}
                          onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                          placeholder="Full legal name on the account"
                          required
                          data-testid="stripe-account-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input
                          value={bankForm.bank_name}
                          onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                          placeholder="e.g., TD Bank, Barclays, Deutsche Bank"
                          required
                          data-testid="stripe-bank-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{getRoutingLabel(bankForm.currency)}</Label>
                          <Input
                            value={bankForm.routing_number}
                            onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value })}
                            placeholder={bankForm.currency === 'EUR' ? 'e.g., DE89370400440532013000' : 'e.g., 021000021'}
                            required
                            data-testid="stripe-routing-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{getAccountLabel(bankForm.currency)}</Label>
                          <Input
                            value={bankForm.account_number}
                            onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                            placeholder={bankForm.currency === 'EUR' ? 'e.g., DEUTDEDBFRA' : 'e.g., 12345678'}
                            required
                            data-testid="stripe-account-number-input"
                          />
                        </div>
                      </div>
                      {bankForm.currency !== 'EUR' && (
                        <div className="space-y-2">
                          <Label>Account Type</Label>
                          <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}>
                            <SelectTrigger data-testid="stripe-account-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="checking">Checking</SelectItem>
                              <SelectItem value="savings">Savings</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {bankForm.currency === 'EUR' && (
                        <div className="space-y-2">
                          <Label>SWIFT / BIC Code (optional)</Label>
                          <Input
                            value={bankForm.swift_code}
                            onChange={(e) => setBankForm({ ...bankForm, swift_code: e.target.value })}
                            placeholder="e.g., DEUTDEFF"
                            data-testid="stripe-swift-input"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Password - shared */}
                  <div className="space-y-2">
                    <Label>Password Verification</Label>
                    <Input
                      type="password"
                      value={bankForm.password}
                      onChange={(e) => setBankForm({ ...bankForm, password: e.target.value })}
                      placeholder="Enter your password to confirm"
                      required
                      data-testid="password-verification-input"
                    />
                    <p className="text-xs text-muted-foreground">For security, enter your password to add a bank account</p>
                  </div>

                  {/* Step-up OTP — required by backend guard */}
                  <div className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3">
                    <Label>Phone Verification (OTP)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={8}
                        value={bankForm.otp_code}
                        onChange={(e) => setBankForm({ ...bankForm, otp_code: e.target.value.replace(/\D/g, '') })}
                        placeholder="6-digit code"
                        data-testid="banking-otp-input"
                        disabled={!otpSent}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant={otpSent ? 'outline' : 'default'}
                        onClick={sendBankingOTP}
                        disabled={otpSending || otpCooldown > 0}
                        data-testid="banking-send-otp-btn"
                      >
                        {otpSending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {otpCooldown > 0 ? `Resend (${otpCooldown}s)` : otpSent ? 'Resend Code' : 'Send Code'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {otpSent
                        ? 'Enter the code we just sent to your registered phone.'
                        : "Tap 'Send Code' — we'll text a one-time code to the phone on your account."}
                    </p>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowAddBankDialog(false)}>Cancel</Button>
                    <Button type="submit" disabled={submitting} data-testid="save-bank-btn">
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Account
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Delete-bank confirmation dialog — same step-up as save (password + OTP). */}
            <Dialog
              open={deleteDialog.open}
              onOpenChange={(open) => {
                if (!open) {
                  setDeleteDialog({ open: false, bankingId: null, password: '', otp_code: '', submitting: false });
                  setOtpSent(false);
                  setOtpCooldown(0);
                }
              }}
            >
              <DialogContent data-testid="delete-bank-dialog">
                <DialogHeader>
                  <DialogTitle>Remove Bank Account</DialogTitle>
                  <DialogDescription>
                    For your security, confirm your password and a one-time code sent to your phone before we remove this account.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={deleteDialog.password}
                      onChange={(e) => setDeleteDialog((d) => ({ ...d, password: e.target.value }))}
                      placeholder="Your account password"
                      data-testid="delete-password-input"
                    />
                  </div>
                  <div className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3">
                    <Label>Phone Verification (OTP)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={8}
                        value={deleteDialog.otp_code}
                        onChange={(e) =>
                          setDeleteDialog((d) => ({ ...d, otp_code: e.target.value.replace(/\D/g, '') }))
                        }
                        placeholder="6-digit code"
                        disabled={!otpSent}
                        className="flex-1"
                        data-testid="delete-otp-input"
                      />
                      <Button
                        type="button"
                        variant={otpSent ? 'outline' : 'default'}
                        onClick={sendBankingOTP}
                        disabled={otpSending || otpCooldown > 0}
                        data-testid="delete-send-otp-btn"
                      >
                        {otpSending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {otpCooldown > 0 ? `Resend (${otpCooldown}s)` : otpSent ? 'Resend Code' : 'Send Code'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {otpSent
                        ? 'Enter the code we just sent to your registered phone.'
                        : "Tap 'Send Code' — we'll text a one-time code to the phone on your account."}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteDialog({ open: false, bankingId: null, password: '', otp_code: '', submitting: false });
                      setOtpSent(false);
                      setOtpCooldown(0);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteBank}
                    disabled={deleteDialog.submitting}
                    data-testid="delete-confirm-btn"
                  >
                    {deleteDialog.submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Remove Account
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {bankingInfo.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No payout accounts added yet</p>
                <p className="text-sm">Add a bank account to withdraw your earnings</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bankingInfo.map(bank => (
                  <div key={bank.banking_id} className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${bank.payout_method === 'stripe' ? 'bg-[#635bff]/10' : 'bg-[#00c3f7]/10'}`}>
                        {bank.payout_method === 'stripe'
                          ? <Globe className="w-5 h-5 text-[#635bff]" />
                          : <Building2 className="w-5 h-5 text-[#00c3f7]" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">{bank.bank_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {bank.account_number?.replace(/(.{4})$/, '••••')} • {bank.account_name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs py-0 h-5">
                            {bank.payout_method === 'stripe' ? 'Stripe' : 'Paystack'}
                          </Badge>
                          <Badge variant="outline" className="text-xs py-0 h-5">
                            {bank.currency || 'NGN'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {bank.is_primary && (
                        <Badge variant="outline" className="text-green-500 border-green-500/30">Primary</Badge>
                      )}
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => openDeleteDialog(bank.banking_id)}
                        data-testid={`delete-bank-${bank.banking_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Approval-gate UI (organizer only). Sits below the existing list/form. */}
            {!isAdmin && !bankRequestLoading && bankRequestStatus.status === 'none' && (
              <div className="mt-6 p-4 rounded-lg border bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="bank-request-gate-none">
                <p className="text-sm text-muted-foreground">
                  You need admin approval before adding banking information.
                </p>
                <Button
                  onClick={submitBankRequest}
                  disabled={bankRequestSubmitting}
                  data-testid="bank-request-submit-btn"
                >
                  {bankRequestSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Request Access
                </Button>
              </div>
            )}
            {!isAdmin && !bankRequestLoading && bankRequestStatus.status === 'pending' && (
              <div className="mt-6 p-4 rounded-lg border bg-amber-500/5 border-amber-500/30" data-testid="bank-request-gate-pending">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Your request is pending review. We&apos;ll notify you once it&apos;s approved.
                </p>
              </div>
            )}
            {!isAdmin && !bankRequestLoading && bankRequestStatus.status === 'rejected' && (
              <div className="mt-6 p-4 rounded-lg border bg-destructive/5 border-destructive/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="bank-request-gate-rejected">
                <p className="text-sm">
                  <span className="text-destructive font-medium">Your request was declined:</span>{' '}
                  <span className="text-muted-foreground">{bankRequestStatus.rejection_reason || 'No reason provided.'}</span>
                </p>
                <Button
                  variant="outline"
                  onClick={submitBankRequest}
                  disabled={bankRequestSubmitting}
                  data-testid="bank-request-resubmit-btn"
                >
                  {bankRequestSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Submit New Request
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Withdraw Button */}
        {bankingInfo.length > 0 && earnings?.available_balance > 0 && (
          <Card className="mb-8 border-primary">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Request Withdrawal</h3>
                  <p className="text-sm text-muted-foreground">
                    Available balance: {earnings.available_balance.toLocaleString()}
                  </p>
                </div>
                <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                  <DialogTrigger asChild>
                    <Button size="lg" data-testid="withdraw-btn">
                      <ArrowDownToLine className="w-4 h-4 mr-2" />
                      Withdraw Funds
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Withdraw Funds</DialogTitle>
                      <DialogDescription>Enter the amount to withdraw to your primary payout account.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleWithdraw} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number" value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          placeholder="Enter amount" min="100"
                          max={earnings?.available_balance || 0}
                          required data-testid="withdraw-amount-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Maximum: {earnings?.available_balance?.toLocaleString() || 0}
                        </p>
                      </div>
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Withdrawal requests are processed within 24-48 hours.
                        </AlertDescription>
                      </Alert>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowWithdrawDialog(false)}>Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="confirm-withdraw-btn">
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Confirm Withdrawal
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Withdrawal History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Withdrawal History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {withdrawals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No withdrawal history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {withdrawals.map(withdrawal => (
                  <div key={withdrawal.withdrawal_id} className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <p className="font-medium">{withdrawal.amount.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">
                        {withdrawal.banking_info?.bank_name} • {withdrawal.banking_info?.account_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(withdrawal.requested_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    {getStatusBadge(withdrawal.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
